import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowDownIcon, CheckIcon, ShieldCheckIcon, XIcon } from 'lucide-react';
import { initMetaPixel, trackPageView } from '../utils/metaPixel';
import { trackPage } from '../utils/analytics';
import { UrgencyCountdownBar } from '../components/cdv/UrgencyCountdownBar';
import { LoomEmbed } from '../components/cdv/LoomEmbed';
import { CdvCtaButton } from '../components/cdv/CdvCtaButton';
import { CdvFaq } from '../components/cdv/CdvFaq';
import { InsideAcademy } from '../components/cdv/InsideAcademy';
import { OfferAndPricing } from '../components/cdv/OfferAndPricing';
import { WhatsAppFloatButton } from '../components/cdv/WhatsAppFloatButton';
import { stages, testimonials, VSL_LOOM_ID } from '../data/academiaCdv';
const ease: [number, number, number, number] = [0.23, 1, 0.32, 1];
export function AcademiaCDV() {
  useEffect(() => {
    initMetaPixel();
    trackPageView();
    trackPage('/academia-cdv');
  }, []);
  return <div className="min-h-screen w-full bg-black font-sans text-white" data-id="element-199" data-mp="3my0v584ksys4">
      <UrgencyCountdownBar data-id="element-200" data-mp="3mvo507onzi3z" />

      <main data-id="element-201" data-mp="3rw2elu5wz044">
        {/* HERO */}
        <section className="px-4 sm:px-6 pt-5 pb-14 sm:pt-8 lg:pt-10 lg:pb-16" data-id="element-202" data-mp="3rx3ovwf3viks">
          <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-[1fr_1.15fr] lg:grid-rows-[auto_auto] gap-x-12 items-center" data-id="element-203" data-mp="p9deqb4f0zl">
            {/* Copy */}
            <motion.div initial={{
            opacity: 0,
            y: 12
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.3,
            ease
          }} className="text-center lg:text-left lg:col-start-1 lg:row-start-1 lg:self-end" data-id="element-204" data-mp="9imck9zs2yu4">
              <p className="text-[0.65rem] sm:text-xs font-bold uppercase tracking-[0.2em] text-gold-metallic" data-id="element-205" data-mp="jepfvj6d93in">
                Academia de Bienes Raíces
              </p>
              <h1 className="mt-2 sm:mt-3 font-sans text-xl sm:text-3xl lg:text-4xl xl:text-[2.6rem] font-extrabold uppercase leading-[1.12]" data-id="element-206" data-mp="2l9gkt9tiz8iz">
                Genera el ingreso de{' '}
                <span className="text-gold-metallic" data-id="element-207" data-mp="2i7lle7qqif9u">todo un año</span> en una
                sola operación de Bienes Raíces
              </h1>
              <p className="mt-2 sm:mt-3 text-sm sm:text-lg font-semibold text-white/90" data-id="element-208" data-mp="2d9jzlf1xc1uo">
                En menos de 90 días. Incluso si empiezas desde cero y sin
                capital infinito.
              </p>
              <p className="hidden sm:block mt-3 text-base text-white/65 leading-relaxed lg:max-w-xl" data-id="element-209" data-mp="315emr5lpmh54">
                El Sistema de 3 Etapas con IA que está permitiendo a
                profesionales en LatAm construir patrimonio real mientras otros
                solo ven cómo la inflación devora sus ahorros.
              </p>
            </motion.div>

            {/* Video */}
            <div className="mt-4 sm:mt-6 lg:mt-0 lg:col-start-2 lg:row-start-1 lg:row-span-2 w-full max-w-3xl mx-auto" data-id="element-210" data-mp="35yh8irk8wmar">
              <p className="mb-2 flex items-center justify-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-white/70" data-id="element-211" data-mp="3fukksq8ta1bf">
                Mira el video
                <ArrowDownIcon className="h-3.5 w-3.5 text-gold-metallic" aria-hidden="true" data-id="element-212" data-mp="2b4b2p8etlia" />
              </p>
              <LoomEmbed loomId={VSL_LOOM_ID} title="Video: Academia de Bienes Raíces" className="shadow-2xl" data-id="element-213" data-mp="3pgvb36pq6yi7" />
            </div>

            {/* Price + CTA */}
            <div className="mt-4 sm:mt-6 lg:mt-6 lg:col-start-1 lg:row-start-2 lg:self-start w-full max-w-2xl mx-auto lg:mx-0" data-id="element-214" data-mp="3fqsiv8qfhokx">
              <p className="hidden sm:flex mb-4 flex-wrap items-center justify-center lg:justify-start gap-x-3 text-sm text-white/70" data-id="element-215" data-mp="36rf7gaiqupxk">
                <span data-id="element-216" data-mp="3as8y3bnkmiu5">
                  <span className="font-bold text-gold-metallic" data-id="element-217" data-mp="3atjxz0dwb6fz">$47/mes</span>{' '}
                  por tiempo limitado
                </span>
                <span className="h-3.5 w-px bg-white/20" aria-hidden="true" data-id="element-218" data-mp="31scaldcx3q0o" />
                <span data-id="element-219" data-mp="31ueynruif92r">
                  o <span className="font-bold text-white" data-id="element-220" data-mp="804cffdwsqdp">$247/año</span> con
                  todo desbloqueado
                </span>
              </p>
              <CdvCtaButton trackingId="cdv_hero_click" data-id="element-221" data-mp="3zam6fczuzhn" />
            </div>
          </div>
        </section>

        {/* TRANSFORMACIÓN */}
        <section className="px-5 pb-16 md:pb-20" data-id="element-222" data-mp="3qed6svi9bemb">
          <div className="max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-px overflow-hidden rounded-2xl bg-white/10" data-id="element-223" data-mp="3qdka6nd0i5hw">
            <div className="flex items-center gap-4 bg-black p-6 md:p-8" data-id="element-224" data-mp="3ld61d33l6rit">
              <XIcon className="h-7 w-7 flex-shrink-0 text-[#FF4D5E]" aria-hidden="true" data-id="element-225" data-mp="3gemcattp4zm8" />
              <div data-id="element-226" data-mp="3cgf4ioh7n8n6">
                <p className="text-xs font-bold uppercase tracking-wider text-white/50" data-id="element-227" data-mp="37g0xn9rci4qq">
                  Pasa de
                </p>
                <p className="mt-1 text-lg text-white/70" data-id="element-228" data-mp="1alwgr1x7iv3u">
                  No tener ingresos en Bienes Raíces
                </p>
              </div>
            </div>
            <div className="flex items-center gap-4 bg-[#0B0B0B] p-6 md:p-8" data-id="element-229" data-mp="1d85joj4ybp19">
              <CheckIcon className="h-7 w-7 flex-shrink-0 text-gold-metallic" aria-hidden="true" data-id="element-230" data-mp="1i6qh6astjum0" />
              <div data-id="element-231" data-mp="1myrqpoylr78c">
                <p className="text-xs font-bold uppercase tracking-wider text-gold-metallic" data-id="element-232" data-mp="1my8jb26kw18k">
                  A tener
                </p>
                <p className="mt-1 text-lg font-semibold text-white" data-id="element-233" data-mp="u91c7et263bp">
                  Múltiples fuentes de ingreso en Bienes Raíces
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* GANCHO Y DOLOR */}
        <section className="px-5 py-16 md:py-24 border-t border-white/10" data-id="element-234" data-mp="145e6adsimj6z">
          <div className="max-w-3xl mx-auto space-y-6 text-lg md:text-xl leading-relaxed text-white/75" data-id="element-235" data-mp="189lrw23ofj0z">
            <p className="font-sans text-2xl md:text-3xl font-bold text-white leading-snug" data-id="element-236" data-mp="2flcgikzfjb9s">
              Si estás aquí, probablemente quieres cambiar tu situación
              financiera a través de los bienes raíces.
            </p>
            <p data-id="element-237" data-mp="kkskolv0k2j5">
              Tal vez estás empezando desde cero y no sabes por dónde comenzar.
              O ya has realizado algunas operaciones, pero sientes que tus
              resultados están muy por debajo de lo que realmente podrías
              conseguir.
            </p>
            <p data-id="element-238" data-mp="olcjruz5dg7x">
              Trabajas 40, 50 o 60 horas a la semana, ahorras lo que puedes,
              pero sientes que tu cuenta bancaria es un balde con agujeros.
              Miras los precios de las propiedades subir y sientes que el tren
              de la libertad financiera se aleja.
            </p>
            <p data-id="element-239" data-mp="ammkq9srp6gx">
              Peor aún, ves a "gurús" prometiendo rentas mágicas, pero cuando
              entras, solo hay teoría de hace 20 años que no funciona en la
              economía de 2026. Sin comunidad que te respalde y sin los
              contratos que protejan tu capital.
            </p>
            <p className="border-l-4 border-[#C8102E] pl-5 text-white" data-id="element-240" data-mp="1osrcc6j26vup">
              El costo de no actuar hoy no es solo dinero; es el tiempo que
              nunca recuperarás con tu familia.
            </p>
          </div>
        </section>

        {/* REVELACIÓN: 3 ETAPAS */}
        <section className="px-5 py-16 md:py-24 bg-[#0B0B0B] border-y border-white/10" data-id="element-241" data-mp="1fpyko25ysi4c">
          <div className="max-w-5xl mx-auto" data-id="element-242" data-mp="1kre3dnko6cjk">
            <div className="max-w-3xl" data-id="element-243" data-mp="28g75k1rvxgmm">
              <h2 className="font-sans text-2xl md:text-4xl font-bold uppercase leading-tight mb-6" data-id="element-244" data-mp="28h8f21t95v28">
                No necesitas pasar años aprendiendo por prueba y error
              </h2>
              <p className="text-lg text-white/70 leading-relaxed" data-id="element-245" data-mp="1yhrd9vh4g1bj">
                Desarrollamos un sistema paso a paso para construir un negocio
                inmobiliario rentable, escalable y sistematizado. Nuestra
                metodología sigue tres etapas muy claras:
              </p>
            </div>

            <ol className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-px bg-white/10 rounded-2xl overflow-hidden" data-id="element-246" data-mp="s3s1qkv05hzc">
              {stages.map((stage, i) => <li key={stage.name} className="flex flex-col bg-[#0B0B0B] p-6 md:p-8" data-id="element-247" data-mp="1eqp8xfpqe5yv" data-mpi={i}>
                  <p className="text-sm font-bold uppercase tracking-wider text-gold-metallic" data-id="element-248" data-mp="19s475butze8b">
                    Etapa {i + 1}
                  </p>
                  <p className="mt-2 font-sans text-2xl font-bold text-white" data-id="element-249" data-mp="22h2v4plmxalj">
                    {stage.name}
                  </p>
                  <p className="mt-3 text-white/65 leading-relaxed" data-id="element-250" data-mp="1xhyl5wqvz8u5">
                    {stage.description}
                  </p>
                  <p className="mt-auto pt-4 text-sm font-semibold text-white" data-id="element-251" data-mp="1sodkfwxws0r9">
                    {stage.goal}
                  </p>
                </li>)}
            </ol>

            <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-8 text-lg leading-relaxed" data-id="element-252" data-mp="2l940maswzz3x">
              <p className="text-white/70" data-id="element-253" data-mp="2ge7vqa2kxh9e">
                La verdadera libertad financiera no depende únicamente de vender
                propiedades. También depende de construir ingresos recurrentes.
                Y con{' '}
                <strong className="text-white" data-id="element-254" data-mp="2xpwp165y0elz">Inteligencia Artificial</strong>{' '}
                puedes encontrar en 10 minutos lo que a un agente tradicional le
                toma un mes.
              </p>
              <p className="font-sans text-xl md:text-2xl font-bold text-gold-metallic leading-snug" data-id="element-255" data-mp="37om5qvm3rt0n">
                No se trata únicamente de cerrar más operaciones. Se trata de
                cerrar mejores operaciones.
              </p>
            </div>
          </div>
        </section>

        <InsideAcademy data-id="element-256" data-mp="3lfqxzcn55j0y" />

        {/* PRUEBA SOCIAL */}
        <section aria-labelledby="testimonios-heading" className="px-5 py-16 md:py-24 border-t border-white/10" data-id="element-257" data-mp="3lhbsj29gh1yt">
          <div className="max-w-6xl mx-auto" data-id="element-258" data-mp="3vcvo1nk3xyn1">
            <div className="max-w-3xl mx-auto text-center mb-12" data-id="element-259" data-mp="3vdn7k00fk5wy">
              <h2 id="testimonios-heading" className="font-sans text-2xl md:text-4xl font-bold uppercase leading-tight mb-4" data-id="element-260" data-mp="2mcgv6jrx3xho">
                No desde la teoría. Desde la práctica.
              </h2>
              <p className="text-lg text-white/70 leading-relaxed" data-id="element-261" data-mp="2va11460q6hym">
                No quiero que solamente escuches lo que yo digo. Prefiero que
                veas los resultados de quienes ya aplicaron este sistema.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-12" data-id="element-262" data-mp="39zyt6x1rzrt3">
              {testimonials.map(t => <article key={t.loomId} className="flex flex-col" data-id="element-263" data-mp="3e34xw098paxg">
                  <LoomEmbed loomId={t.loomId} title={`Testimonio: ${t.title}`} data-id="element-264" data-mp="3j1yazrkm6zx3" />
                  <h3 className="mt-4 font-sans text-lg md:text-xl font-bold text-white" data-id="element-265" data-mp="1jqf3lheylzpj">
                    "{t.title}"
                  </h3>
                  <p className="mt-1 text-sm text-white/60" data-id="element-266" data-mp="1h4o3xgsaok9j">
                    {t.name}
                    {t.location && ` · ${t.location}`}
                  </p>
                  {t.result && <p className="mt-2 text-sm font-semibold text-gold-metallic" data-id="element-267" data-mp="17dtfq6vhnalg">
                      Resultado: {t.result}
                    </p>}
                </article>)}
            </div>
          </div>
        </section>

        <OfferAndPricing data-id="element-268" data-mp="200xlwf6bhuyt" />

        {/* FAQ */}
        <section aria-labelledby="faq-heading" className="px-5 py-16 md:py-24 bg-[#0B0B0B] border-y border-white/10" data-id="element-269" data-mp="1v3nrel42mt44">
          <div className="max-w-3xl mx-auto" data-id="element-270" data-mp="1q6xbbxs9nxip">
            <h2 id="faq-heading" className="font-sans text-2xl md:text-4xl font-bold uppercase leading-tight mb-8" data-id="element-271" data-mp="1m40mhpc4v56b">
              Preguntas frecuentes
            </h2>
            <CdvFaq data-id="element-272" data-mp="an0vqy5foyee" />
          </div>
        </section>

        {/* GARANTÍA */}
        <section className="px-5 py-16 md:py-20" data-id="element-273" data-mp="c4pvu2q4xzfb">
          <div className="max-w-3xl mx-auto flex flex-col sm:flex-row items-center sm:items-start gap-6 text-center sm:text-left" data-id="element-274" data-mp="c60ujo4q4gzk">
            <ShieldCheckIcon className="h-16 w-16 flex-shrink-0 text-gold-metallic" aria-hidden="true" data-id="element-275" data-mp="27bpwx1u8t19" />
            <div data-id="element-276" data-mp="74v0nmzbunog">
              <h2 className="font-sans text-2xl md:text-3xl font-bold uppercase mb-3" data-id="element-277" data-mp="74bm8g8vyhf6">
                El riesgo es prácticamente cero
              </h2>
              <p className="text-lg text-white/70 leading-relaxed" data-id="element-278" data-mp="3ub46nst3xt9h">
                Si por cualquier motivo sientes que la comunidad no es para ti,
                puedes cancelar tu membresía cuando quieras. No existe
                permanencia forzosa. Y si en los primeros 7 días decides que los
                bienes raíces no son lo tuyo, te devolvemos tu dinero.
              </p>
            </div>
          </div>
        </section>

        {/* CIERRE */}
        <section className="px-5 py-16 md:py-24 border-t border-white/10" data-id="element-279" data-mp="3aopnpzth3qy8">
          <div className="max-w-5xl mx-auto" data-id="element-280" data-mp="2ryuozaaj0muv">
            <h2 className="font-sans text-2xl md:text-4xl font-bold uppercase leading-tight mb-10 text-center" data-id="element-281" data-mp="2mzrkps2dekny">
              Tienes dos caminos frente a ti
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-282" data-mp="30wwlpdn88tcc">
              <div className="rounded-2xl border border-white/10 p-6 md:p-8" data-id="element-283" data-mp="2vympr0tohopo">
                <p className="flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-white/50" data-id="element-284" data-mp="3awf3pojku51r">
                  <XIcon className="h-4 w-4" aria-hidden="true" data-id="element-285" data-mp="35xki6eu862dy" /> Camino A
                </p>
                <p className="mt-3 text-white/60 leading-relaxed" data-id="element-286" data-mp="3joh1ff29h78c">
                  Seguir intentando ahorrar en un mercado que castiga a los
                  ahorradores, viendo cómo otros se quedan con las mejores
                  oportunidades.
                </p>
              </div>
              <div className="rounded-2xl border-2 border-gold-metallic bg-white/[0.03] p-6 md:p-8" data-id="element-287" data-mp="3opwkj1kvp3o3">
                <p className="flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-gold-metallic" data-id="element-288" data-mp="ckiq70rxmchc">
                  <CheckIcon className="h-4 w-4" aria-hidden="true" data-id="element-289" data-mp="glm5nm62i6i1" /> Camino B
                </p>
                <p className="mt-3 text-white leading-relaxed" data-id="element-290" data-mp="lefd4hsx7sxt">
                  Unirte a la Academia, apalancarte en nuestra tecnología y
                  comunidad, y construir un legado que te permita generar el
                  ingreso de un año en una sola operación.
                </p>
              </div>
            </div>

            <p className="mt-12 text-center text-lg md:text-xl text-white/80" data-id="element-291" data-mp="2ne4t35wul1l">
              La oportunidad de transformar tu futuro financiero puede comenzar
              con la decisión que tomes hoy. El reloj marca las{' '}
              <strong className="text-white" data-id="element-292" data-mp="1e3cixlfy3x9o">
                11:59 PM del lunes 12 de octubre
              </strong>
              .
            </p>
            <div className="mt-8 max-w-2xl mx-auto" data-id="element-293" data-mp="1j3qsx913xhac">
              <CdvCtaButton trackingId="cdv_cierre_click" data-id="element-294" data-mp="33jycbpvndzu4" />
            </div>
            <p className="mt-8 text-center font-heading text-xl italic text-gold-metallic" data-id="element-295" data-mp="2ykviz2p15i1z">
              Nos vemos dentro de la comunidad de la Academia de Bienes Raíces.
            </p>
          </div>
        </section>
      </main>

      <WhatsAppFloatButton data-id="element-296" data-mp="2otqzpqsbnb1c" />

      {/* FOOTER */}
      <footer className="w-full border-t border-white/10" data-id="element-297" data-mp="2kt76gyavcrkh">
        <div className="max-w-5xl mx-auto px-4 py-8 md:py-10 grid grid-cols-1 md:grid-cols-3 gap-6 items-center text-center md:text-left" data-id="element-298" data-mp="2kus10nx6oaic">
          <div data-id="element-299" data-mp="2av16im8mhvi9">
            <p className="text-lg md:text-2xl font-bold" data-id="element-300" data-mp="2auhmmzzewx1l">
              Bienes Raíces Academy
            </p>
            <p className="text-sm md:text-lg text-white/80" data-id="element-301" data-mp="25wpvi86ldeyg">
              Por Susana Sánchez G
            </p>
          </div>
          <div className="text-center leading-tight" data-id="element-302" data-mp="92cw9sxka7sz">
            <p className="text-base md:text-xl font-bold tracking-[0.15em] text-white" data-id="element-303" data-mp="44tam7yjasr0">
              ACADEMIA
            </p>
            <p className="text-lg md:text-2xl font-extrabold tracking-wide text-gold-metallic" data-id="element-304" data-mp="1yjqnleuc2k6v">
              BIENES RAÍCES
            </p>
          </div>
          <p className="text-xs text-white/60 md:text-right" data-id="element-305" data-mp="23dbaaaqh7rqr">
            Todos los Derechos Reservados
            <br data-id="element-306" data-mp="1tloue13714ho" />
            2026
          </p>
        </div>
      </footer>
    </div>;
}